import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { ArrowLeft, LifeBuoy, Mail, MessageSquare } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import HelpdeskWidget from "@/components/support/HelpdeskWidget";


const FAQS = [
  {
    q: "How does my $39 lifetime license work?",
    a: "One payment unlocks the full platform forever — every source, every blueprint, every feature we ship. No recurring billing, no seat limits, no usage caps.",
  },
  {
    q: "How often is the trend feed updated?",
    a: "Our scanners run daily across 35+ sources (search, social, marketplaces, communities). New trends appear in your feed within a few hours of being detected.",
  },
  {
    q: "What's included in a trend blueprint?",
    a: "Buyer profile, price band, 3 product angles, supplier or template list, competition snapshot, and a 7-day launch plan — everything you need to act, not just observe.",
  },
  {
    q: "How do I set up alerts for new trends?",
    a: "Go to your Dashboard → Alerts. Add a keyword or niche, choose a velocity threshold, and pick email or in-app delivery. You'll be notified the moment a matching trend crosses your threshold.",
  },
  {
    q: "I forgot my password — what do I do?",
    a: "Use the 'Forgot password' link on the sign-in page. You'll get a reset email within a minute. If it doesn't arrive, check spam, then contact us below.",
  },
  {
    q: "How do I cancel or get a refund?",
    a: "Since the license is a one-time purchase, there's nothing to cancel. If you're within the 14-day window and unhappy, email us via the form below and we'll refund the full $39 — no questions.",
  },
  {
    q: "Can I use Trends Discovery for client work?",
    a: "Yes. Your license covers unlimited personal and commercial use. Just don't resell raw blueprint exports as a competing product.",
  },
  {
    q: "How is my data handled?",
    a: "Your account data, watches, and alerts are private to you. We use privacy-friendly analytics (no cookies, no cross-site tracking) and never sell user data.",
  },
  {
    q: "Which payment methods are supported?",
    a: "All major credit and debit cards via Stripe. Local payment methods (Apple Pay, Google Pay, SEPA, iDEAL) are auto-detected based on your region at checkout.",
  },
  {
    q: "I found a bug or have a feature request — where do I send it?",
    a: "Use the contact form below and pick 'Bug report' or 'Feature request' as the subject. We read every message and ship fixes weekly.",
  },
];

const ticketSchema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
  subject: z.string().trim().min(3, "Subject is too short").max(200),
  message: z.string().trim().min(10, "Tell us a bit more").max(5000),
});

export default function Support() {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = ticketSchema.safeParse({ email, subject, message });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("support_tickets").insert({
      user_id: user?.id ?? null,
      email: parsed.data.email,
      subject: parsed.data.subject,
      message: parsed.data.message,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Couldn't send your message. Please try again.");
      return;
    }
    trackEvent("Support Ticket Submitted", { hasAccount: !!user });
    setSent(true);
    setSubject("");
    setMessage("");
    toast.success("Message sent — we'll get back to you within 24h.");
  };

  return (
    <>
      <Helmet>
        <title>Support & FAQ — Trends Discovery</title>
        <meta name="description" content="Questions about your lifetime license, the trend feed, or alerts? Read the FAQ or send the team a message — replies within 24 hours." />
        <link rel="canonical" href="https://trendsdiscovery.com/support" />
        <meta property="og:title" content="Support & FAQ — Trends Discovery" />
        <meta property="og:description" content="Questions about your lifetime license, the trend feed, or alerts? Read the FAQ or send the team a message — replies within 24 hours." />
        <meta property="og:url" content="https://trendsdiscovery.com/support" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
      </Helmet>
    <div className="min-h-screen">
      <header className="border-b border-border">
        <div className="container flex items-center justify-between py-4">
          <Link
            to={user ? "/dashboard" : "/"}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back
          </Link>
          <div className="flex items-center gap-2 font-display font-bold">
            <LifeBuoy className="size-5 text-primary" />
            Help & Support
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="container max-w-3xl py-10 sm:py-16 space-y-16">
        <section className="text-center space-y-4">
          <div className="font-mono text-xs text-primary">// SUPPORT</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">
            How can we help?
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Ask the assistant for instant answers grounded in our docs, browse the FAQ, or
            send us a message — replies within 24 hours, Monday to Friday.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
            <MessageSquare className="size-5 text-primary" />
            Chat with the assistant
          </h2>
          <HelpdeskWidget variant="inline" />
        </section>


        <section>
          <h2 className="font-display text-2xl font-bold mb-6 flex items-center gap-2">
            <MessageSquare className="size-5 text-primary" />
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-xl px-5 bg-card"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold mb-1 flex items-center gap-2">
            <Mail className="size-5 text-primary" />
            Contact us
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Can't find what you're looking for? Send a message and we'll get back to
            you.
          </p>

          {sent ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center space-y-3">
              <div className="font-display text-lg font-bold">Message received</div>
              <p className="text-sm text-muted-foreground">
                Thanks — we'll reply to <span className="font-mono">{email}</span>{" "}
                within 24 hours.
              </p>
              <Button variant="outline" size="sm" onClick={() => setSent(false)}>
                Send another
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="support-email">Your email</Label>
                <Input
                  id="support-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-subject">Subject</Label>
                <Input
                  id="support-subject"
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Bug report, Billing question, Feature request"
                  maxLength={200}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-message">Message</Label>
                <Textarea
                  id="support-message"
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe what's happening, what you expected, and any steps to reproduce."
                  rows={6}
                  maxLength={5000}
                />
                <div className="text-right text-xs text-muted-foreground font-mono">
                  {message.length}/5000
                </div>
              </div>
              <Button type="submit" variant="hero" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </section>
      </main>
    </div>
    </>

  );
}

